import React, { useContext, useState } from 'react';
import {
  Link,
  ModuleSection,
  Stack,
  SystemFeaturesContext,
  TrackEventKey,
  UserContext,
  MetadataContext,
  AnimatePresence,
} from '@/components';
import { ModuleBaseModel, ModuleBaseNameProps, ModuleSectionPropsModel, StatElementModel } from '@/types';
import CaretDownLight from '@/assets/caretDownLight.svg';
import ScissorIcon from '@/assets/scissors-light.svg';
import { motion } from 'motion/react';
import { accordionPanel } from '@/animations';
import ReportModulHeader from '@/components/report/ReportModulHeader';
import { useTranslations } from 'next-intl';
import classNames from 'classnames';
import { convertAbsoluteLinkToRelative, isExternalLink, isStatBankGDLink } from '@/utils';
import FolderSelectionDialog from '@/components/scrapbook/dialogs/FolderSelectionDialog';
import { saveToScrapbook } from '@/api/scrapbook-data';
import { useNotification } from '@/components/general/NotificationProvider';
import { useRouter } from 'next/router';
import GDLogo from '@/assets/gd-logo.svg';

type StatsListModuleModel = ModuleBaseModel<
  {
    collapseStats: boolean;
    stats: ({
      title: string;
      statElements: StatElementModel[];
    } & ModuleBaseNameProps)[];
    title: string;
  } & ModuleSectionPropsModel
>;

const StatsListModule = (data: StatsListModuleModel) => {
  const { collapseStats, stats } = data.props;
  const { realHost, staffPdfLink, saveToScrapbook: saveToScrapbookFeature } = useContext(SystemFeaturesContext);
  const user = useContext(UserContext);
  const { pageId, pageTitle, pageUrl } = useContext(MetadataContext);
  const { showNotification } = useNotification();
  const { asPath } = useRouter();
  const [activeIndexList, setActiveIndexList] = useState<number[]>(() => {
    if (typeof window !== 'undefined') {
      const savedState = sessionStorage.getItem(`statsList-active-index:${asPath}`);
      if (savedState !== null) {
        return [JSON.parse(savedState)];
      }
    }
    return [0];
  });

  const [selectedItems, setSelectedItems] = useState<StatElementModel[]>([]);
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [saveObject, setSaveObject] = useState<any>(null);
  const t = useTranslations();

  const toggle = (index: number) => {
    let newArray = [...activeIndexList];
    if (newArray.includes(index)) {
      newArray = newArray.filter((i) => i !== index);
    } else {
      newArray = [index, ...newArray];
    }
    setActiveIndexList(newArray);
  };

  const setActiveIndexListSingle = (index: number) => {
    setActiveIndexList([index]);
    saveActiveIndex(index);
  };

  const deselectItems = () => {
    setSelectedItems([]);
  };

  const copySelectedItemsToClipboard = () => {
    if (selectedItems.length === 0) {
      alert('No items selected');
      return;
    }

    let formattedHtmlContent = '<html>';
    let formattedContent = '';
    selectedItems.forEach((statElement) => {
      if (statElement.link?.href) {
        formattedHtmlContent += `<p>${statElement.numericValue}</p><p><a href="${statElement.link.href}" target="_blank">${statElement.description}</a></p><br/>`;
        formattedContent += `${statElement.numericValue}\t\n${statElement.description}\n(${statElement.link.href})\n\n`;
      } else {
        formattedHtmlContent += `<p>${statElement.numericValue}</p><p>${statElement.description}</p><br/>`;
        formattedContent += `${statElement.numericValue}\t\n${statElement.description}\n`;
      }
    });
    formattedHtmlContent += '</html>';

    try {
      if (navigator.clipboard && navigator.clipboard.write) {
        navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([formattedHtmlContent], { type: 'text/html' }),
            'text/plain': new Blob([formattedContent], { type: 'text/plain' }),
          }),
        ]);
      }
    } catch (err) {
      console.error('Failed to copy to clipboard: ', err);
    }
  };

  const toggleItemSelection = (item: StatElementModel) => {
    setSelectedItems((prev) => {
      const exists = prev.find((i) => i.numericValue === item.numericValue && i.description === item.description);
      if (exists) {
        return prev.filter((i) => !(i.numericValue === item.numericValue && i.description === item.description));
      } else {
        return [...prev, item];
      }
    });
  };

  const generateHashCode = (text: string) => {
    return text.split('').reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);
  };

  const handleSaveToScrapbook = (statElement: StatElementModel, index: number) => {
    const content = {
      index: index,
      pageId: pageId,
      pageTitle: pageTitle,
      pageUrl: pageUrl,
      numericValue: statElement.numericValue,
      description: statElement.description,
      link: statElement.link,
      statSubjects: statElement.statSubjects,
    };
    const linkObject = {
      path: `stat.${content.pageId}.${generateHashCode(statElement.numericValue + statElement.description)}.json`,
      contentType: 'application/x.stylus.stat+json',
      content: JSON.stringify(content),
    };
    setSaveObject(linkObject);
    setShowFolderDialog(true);
  };

  const saveActiveIndex = (index: number) => {
    try {
      sessionStorage.setItem(`statsList-active-index:${asPath}`, index.toString());
    } catch {
      // ignore
    }
  };

  return (
    <ModuleSection data={data}>
      <Stack className="container">
        <ReportModulHeader
          title={t('report.$keyStats')}
          expanded={!collapseStats}
          eventKey={TrackEventKey.KEY_STAT}
          saveOpenStateKey="statsListModule"
        >
          {staffPdfLink && (
            <div className="mb-[30px] flex flex-wrap gap-4 pt-10 print:hidden">
              <button
                onClick={copySelectedItemsToClipboard}
                className="cursor-pointer rounded-full bg-gray-100 px-4 py-2 text-black hover:bg-gray-200"
              >
                Copy Selected Items
              </button>
              <button
                onClick={deselectItems}
                className="cursor-pointer rounded-full bg-gray-100 px-4 py-2 text-black hover:bg-gray-200"
              >
                Deselect All
              </button>
            </div>
          )}

          {
            // Mobile View
            stats.map((stat, index) => (
              <div
                key={index}
                className="flex flex-col justify-between pt-5 md:hidden md:flex-row md:gap-6 md:pt-10 xl:pt-12 print:flex-row"
              >
                <div
                  className={classNames(
                    'border-from-bg/20 w-full md:w-1/4 md:pb-4 xl:w-1/6 print:w-1/4',
                    activeIndexList.includes(index) && 'border-b pb-5 md:border-b-0',
                  )}
                >
                  <button onClick={() => toggle(index)} className="flex w-full items-center justify-between text-left">
                    <h4
                      className={classNames(
                        activeIndexList.includes(index) ? 'scale-100 font-light' : 'scale-75',
                        'origin-left text-lg transition-all lg:leading-[30px] print:text-2xl print:leading-[30px]',
                      )}
                    >
                      {stat.title}
                    </h4>
                    <CaretDownLight
                      className={classNames(
                        'h-4 w-4 shrink-0 transition-all md:hidden print:hidden',
                        activeIndexList.includes(index) && 'rotate-180',
                      )}
                    />
                  </button>
                </div>

                <motion.div
                  variants={accordionPanel.variants}
                  initial="inactive"
                  animate={activeIndexList.includes(index) ? 'active' : 'inactive'}
                  className="w-full overflow-hidden md:!h-auto md:w-3/4 md:!opacity-100 print:!h-auto print:!opacity-100"
                >
                  <div className="flex flex-col gap-6 pt-5 md:pt-0 lg:gap-3">
                    {stat.statElements.map((statElement, index) => {
                      let finalLink = statElement.link;
                      if (statElement.link) {
                        finalLink = convertAbsoluteLinkToRelative(statElement.link, realHost);
                        if (finalLink && isExternalLink(finalLink)) {
                          finalLink.target = '_blank';
                        }
                      }
                      return (
                        <div
                          key={index}
                          className="border-from-bg/20 flex flex-col items-center justify-between gap-3 border-b pb-3 md:flex-row md:pb-3 print:flex-row"
                        >
                          <div className="flex w-full items-center justify-between md:w-1/4 md:pl-3 xl:w-1/5 print:w-1/4">
                            <div className="text-2xl lg:text-3xl print:text-3xl">{statElement.numericValue}</div>
                            {statElement.link && isStatBankGDLink(statElement.link) && (
                              <div className="px-2 lg:px-5">
                                <GDLogo className="h-6 w-6 fill-[#252628]" />
                              </div>
                            )}
                          </div>
                          <div className="w-full text-base leading-[22px] font-medium md:w-3/4 lg:w-5/6">
                            {finalLink ? (
                              <Link link={finalLink} className="block">
                                {statElement.description}
                              </Link>
                            ) : (
                              statElement.description
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              </div>
            ))
          }

          {
            // Desktop View
          }
          <div className="hidden flex-row justify-between pt-12 md:flex print:hidden">
            <div className="xxl:w-2/12 flex flex-col gap-4 md:w-3/12">
              {stats.map((stat, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndexListSingle(index)}
                  className={classNames('flex w-full cursor-pointer items-center justify-between text-left')}
                >
                  <h4
                    className={classNames(
                      'origin-left text-lg leading-8 transition-all xl:text-[26px]',
                      activeIndexList.includes(index) ? 'scale-100' : 'scale-75 opacity-50',
                    )}
                  >
                    {stat.title}
                  </h4>
                </button>
              ))}
            </div>

            <div className="w-8/12 lg:w-9/12 xl:w-9/12">
              <AnimatePresence>
                {stats[activeIndexList[0]] && (
                  <motion.div
                    initial={{ x: 5, opacity: 0 }}
                    animate={{
                      x: 0,
                      opacity: 1,
                      transition: {
                        delay: 0.2,
                        type: 'spring',
                        damping: 25,
                      },
                    }}
                    exit={{ x: 5, opacity: 0 }}
                    transition={{
                      type: 'tween',
                      ease: 'easeIn',
                      duration: 0.2,
                    }}
                    key={stats[activeIndexList[0]].title}
                    className="flex flex-col gap-6 lg:pr-16"
                  >
                    <div className="flex flex-col gap-6 pt-5 md:pt-0 lg:gap-3">
                      {stats[activeIndexList[0]].statElements.map((statElement, index) => {
                        let finalLink = statElement.link;
                        if (statElement.link) {
                          finalLink = convertAbsoluteLinkToRelative(statElement.link, realHost);
                          if (finalLink && isExternalLink(finalLink)) {
                            finalLink.target = '_blank';
                          }
                        }
                        return (
                          <div
                            key={index}
                            className="group border-from-bg/20 flex flex-col items-center justify-between gap-3 border-b pb-6 md:flex-row md:pb-3 print:flex-row"
                          >
                            {staffPdfLink && (
                              <div className="print:hidden">
                                <input
                                  type="checkbox"
                                  checked={selectedItems.some(
                                    (i) =>
                                      i.numericValue === statElement.numericValue &&
                                      i.description === statElement.description,
                                  )}
                                  onChange={() => toggleItemSelection(statElement)}
                                  className="h-4 w-4 cursor-pointer accent-black"
                                />
                              </div>
                            )}
                            <div className="w-full text-2xl md:w-1/4 md:pl-3 lg:text-3xl xl:w-1/6 print:w-1/4 print:text-3xl">
                              {statElement.numericValue}
                            </div>
                            <div className="flex w-full items-center justify-between text-base leading-[22px] font-medium md:w-3/4 lg:w-5/6">
                              {finalLink ? (
                                <Link link={finalLink} className="block">
                                  {statElement.description}
                                </Link>
                              ) : (
                                statElement.description
                              )}
                              {statElement.link && isStatBankGDLink(statElement.link) && (
                                <div className="px-2">
                                  <GDLogo className="h-7 w-7 fill-[#252628]" />
                                </div>
                              )}
                            </div>

                            {saveToScrapbookFeature && (
                              <div className="opacity-0 transition-opacity group-hover:opacity-100">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSaveToScrapbook(statElement, index);
                                  }}
                                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full hover:bg-gray-100"
                                  title="Save to Scrapbook"
                                >
                                  <ScissorIcon className="h-6 w-6" />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </ReportModulHeader>
      </Stack>

      <FolderSelectionDialog
        isOpen={showFolderDialog}
        onClose={() => setShowFolderDialog(false)}
        onSave={async (folderId: number) => {
          try {
            const result = await saveToScrapbook(folderId, saveObject, user?.userId);
            if (result?.succeeded) {
              showNotification(result.message || 'Stat saved to scrapbook successfully', 'savesuccess');
            } else {
              showNotification('Call to scrapbook server failed.', 'error');
            }
          } catch {
            showNotification('Call to scrapbook server failed.', 'error');
          }
        }}
        reportTitle="Save Stat to Scrapbook"
      />
    </ModuleSection>
  );
};

export default StatsListModule;
